/** How long a tab opened on a blob is given to load it before the URL is released. */
const RELEASE_AFTER_MS: number = 60_000;

/**
 * Opens a fetched file in a new tab -- but only if it is one of the types the caller expects.
 *
 * The endpoints behind this need the bearer token, which a plain link cannot carry, so the
 * file is fetched as a blob and handed to the browser through an object URL.
 *
 * **The type check is the point of this being a function.** A blob URL belongs to the app's
 * own origin, so whatever it holds runs with the app's own session: an HTML or SVG blob
 * opened this way could read `localStorage.authToken`. The API only ever serves a type it
 * found in the file's own bytes, and this is the second lock on the same door -- a response
 * that is not a PDF or a picture is refused rather than rendered.
 *
 * Returns whether it was opened, so the caller can say so when it was not.
 */
export const openBlobInNewTab = (blob: Blob, allowedTypes: string[]): boolean => {
    // "application/pdf;charset=..." is still a PDF.
    const type: string = blob.type.split(';')[0].trim().toLowerCase();

    if (!allowedTypes.includes(type)) {
        return false;
    }

    const url: string = URL.createObjectURL(blob);

    window.open(url, '_blank', 'noopener');

    // Released on a timer: revoking immediately races the new tab's own load.
    setTimeout(() => URL.revokeObjectURL(url), RELEASE_AFTER_MS);

    return true;
};
